import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import axiosClient from '../../api/axiosClient'
import Button from '../../components/Button/Button'
import Typography from '../../components/Typography'
import RectifyInvoiceModal from '../../components/Modal/RectifyInvoiceModal'
import { theme } from '../../theme'
import {
  formatDate,
  formatDateTime,
  formatEuros,
  truncateHash,
  INVOICE_STATUS_LABELS
} from './format'

const AdminInvoiceDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [resending, setResending] = useState(false)
  const [rectifyOpen, setRectifyOpen] = useState(false)

  const loadInvoice = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axiosClient.get(`/api/admin/invoices/${id}`)
      setInvoice(data.data)
    } catch (err) {
      console.error('[AdminInvoiceDetail] load failed', err)
      const status = err?.response?.status
      if (status === 404) toast.error('Factura no encontrada.')
      else toast.error('No pudimos cargar la factura.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadInvoice()
  }, [loadInvoice])

  const previewHtml = async () => {
    try {
      const { data } = await axiosClient.get(`/api/admin/invoices/${id}/preview`, {
        responseType: 'text',
        headers: { Accept: 'text/html' }
      })
      const win = window.open('', '_blank')
      if (win) {
        win.document.open()
        win.document.write(data)
        win.document.close()
      } else {
        toast.error('El navegador bloqueó la ventana emergente. Permite popups para este sitio.')
      }
    } catch (err) {
      console.error('[AdminInvoiceDetail] preview failed', err)
      toast.error('No se pudo generar el preview de la factura.')
    }
  }

  const downloadPdf = async () => {
    try {
      const response = await axiosClient.get(`/api/admin/invoices/${id}/pdf`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${invoice?.invoiceNumber || id}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[AdminInvoiceDetail] PDF fetch failed', err)
      toast.error('No se pudo generar el PDF.')
    }
  }

  const resendEmail = async () => {
    if (resending) return
    setResending(true)
    try {
      const { data } = await axiosClient.post(`/api/admin/invoices/${id}/resend-email`)
      if (data?.success) {
        toast.success('Email reenviado al organizador.')
      } else {
        toast.error('No se pudo generar el PDF o enviar el email. Revisa las alertas.')
      }
      await loadInvoice()
    } catch (err) {
      console.error('[AdminInvoiceDetail] resend failed', err)
      toast.error('No se pudo reenviar el email.')
    } finally {
      setResending(false)
    }
  }

  if (loading) {
    return <Container><Typography as="p">Cargando…</Typography></Container>
  }
  if (!invoice) {
    return (
      <Container>
        <Typography as="p">Factura no encontrada.</Typography>
        <Link to="/admin/invoices">← Volver al listado</Link>
      </Container>
    )
  }

  const isRectificativa = Boolean(invoice.rectificativaOf)

  return (
    <Container>
      <BackBar>
        <Link to="/admin/invoices">← Volver al listado</Link>
      </BackBar>

      <HeaderBlock>
        <div>
          <Typography as="h1" $variant="title-4-semibold">Factura {invoice.invoiceNumber}</Typography>
          <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultWeak}>
            Emitida el {formatDate(invoice.issuedAt)} · Periodo {invoice.invoicePeriod}
          </Typography>
        </div>
        <HeaderActions>
          <StatusChip $status={invoice.status}>{INVOICE_STATUS_LABELS[invoice.status] || invoice.status}</StatusChip>
          <Button size="sm" $variant="outline" onClick={previewHtml}>Previsualizar</Button>
          <Button size="sm" $variant="outline" onClick={downloadPdf}>Descargar PDF</Button>
          <Button size="sm" $variant="outline" onClick={resendEmail} disabled={resending}>
            {resending ? 'Reenviando…' : 'Reenviar email'}
          </Button>
          {invoice.status === 'issued' && !isRectificativa && (
            <Button size="sm" onClick={() => setRectifyOpen(true)}>Emitir rectificativa</Button>
          )}
        </HeaderActions>
      </HeaderBlock>

      {isRectificativa && (
        <RectBanner>
          ⚠️ Esta es una factura <strong>rectificativa</strong>.
          {' '}Original:{' '}
          <Link to={`/admin/invoices/${invoice.rectificativaOf?._id || invoice.rectificativaOf}`}>
            {invoice.rectificativaOf?.invoiceNumber || invoice.rectificativaOf?._id || String(invoice.rectificativaOf)}
          </Link>
          {' · '}Tipo: <strong>{invoice.rectificationType}</strong>
          {invoice.rectificationReason && <> · Motivo: <em>{invoice.rectificationReason}</em></>}
        </RectBanner>
      )}

      <Grid>
        <Card>
          <CardTitle>Emisor (Motorik)</CardTitle>
          <KV><dt>Razón social</dt><dd>{invoice.issuerSnapshot?.legalName || '—'}</dd></KV>
          <KV><dt>NIF</dt><dd>{invoice.issuerSnapshot?.taxId || '—'}</dd></KV>
          <KV><dt>Dirección</dt><dd>{invoice.issuerSnapshot?.address || '—'}</dd></KV>
          <KV><dt>País</dt><dd>{invoice.issuerSnapshot?.country || '—'}</dd></KV>
          <KV><dt>Email</dt><dd>{invoice.issuerSnapshot?.email || '—'}</dd></KV>
          <Frozen>Datos congelados al emitir la factura.</Frozen>
        </Card>

        <Card>
          <CardTitle>Receptor (Organizador)</CardTitle>
          <KV><dt>Razón social</dt><dd>{invoice.recipientSnapshot?.legalName || '—'}</dd></KV>
          <KV><dt>NIF / VAT</dt><dd>{invoice.recipientSnapshot?.taxId || '—'}</dd></KV>
          <KV><dt>Dirección</dt><dd>{invoice.recipientSnapshot?.address || '—'}</dd></KV>
          <KV><dt>País</dt><dd>{invoice.recipientSnapshot?.country || '—'}</dd></KV>
          <KV><dt>Tratamiento IVA</dt><dd>{invoice.recipientSnapshot?.vatTreatment || '—'}</dd></KV>
          <KV><dt>Email</dt><dd>{invoice.recipientSnapshot?.email || '—'}</dd></KV>
          <Frozen>Datos congelados al emitir la factura.</Frozen>
        </Card>
      </Grid>

      <Section>
        <SectionTitle>Líneas</SectionTitle>
        <LinesTable>
          <thead>
            <tr>
              <th>PaymentId</th>
              <th>Descripción</th>
              <th>Base</th>
              <th>% IVA</th>
              <th>IVA</th>
              <th>Total línea</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map((line, i) => (
              <tr key={line._id || i}>
                <td><Mono>{line.paymentId?.toString?.() || line.paymentId || '—'}</Mono></td>
                <td>{line.description || '—'}</td>
                <td>{formatEuros(line.taxableBase)}</td>
                <td>{line.vatRate}%</td>
                <td>{formatEuros(line.vatAmount)}</td>
                <td>{formatEuros(line.lineTotal != null ? line.lineTotal : (line.taxableBase + line.vatAmount))}</td>
              </tr>
            ))}
          </tbody>
        </LinesTable>
      </Section>

      <Section>
        <SectionTitle>Totales</SectionTitle>
        <TotalsGrid>
          <KV><dt>Subtotal</dt><dd>{formatEuros(invoice.subtotal)}</dd></KV>
          <KV><dt>IVA ({invoice.vatRate}%)</dt><dd>{formatEuros(invoice.vatAmount)}</dd></KV>
          <KV><dt>Total</dt><dd><strong>{formatEuros(invoice.total, { currency: invoice.currency || 'EUR' })}</strong></dd></KV>
          <KV><dt>Tratamiento</dt><dd>{invoice.vatTreatment}</dd></KV>
        </TotalsGrid>
      </Section>

      <Section>
        <SectionTitle>Integridad</SectionTitle>
        <TotalsGrid>
          <KV>
            <dt>Hash</dt>
            <dd title={invoice.hash}><Mono>{truncateHash(invoice.hash)}</Mono></dd>
          </KV>
          <KV>
            <dt>Hash anterior</dt>
            <dd title={invoice.previousHash || ''}><Mono>{truncateHash(invoice.previousHash)}</Mono></dd>
          </KV>
          <KV><dt>Algoritmo</dt><dd>{invoice.hashAlgorithm || 'sha256'}</dd></KV>
          <KV><dt>Generado</dt><dd>{formatDateTime(invoice.hashGeneratedAt)}</dd></KV>
        </TotalsGrid>
      </Section>

      <Section>
        <SectionTitle>Entrega</SectionTitle>
        <TotalsGrid>
          <KV><dt>Email enviado</dt><dd>{formatDateTime(invoice.emailSentAt)}</dd></KV>
          <KV><dt>PDF</dt><dd>{invoice.pdfPublicId ? <Mono>{invoice.pdfPublicId}</Mono> : '—'}</dd></KV>
        </TotalsGrid>
      </Section>

      {rectifyOpen && (
        <RectifyInvoiceModal
          originalInvoice={invoice}
          onClose={() => setRectifyOpen(false)}
          onSuccess={(rect) => {
            setRectifyOpen(false)
            navigate(`/admin/invoices/${rect._id}`)
          }}
        />
      )}
    </Container>
  )
}

export default AdminInvoiceDetail

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const BackBar = styled.div`
  a {
    color: ${theme.colors.defaultWeak};
    text-decoration: none;
    font-size: 13px;
    &:hover { color: ${theme.colors.defaultStrong}; }
  }
`

const HeaderBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`

const StatusChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $status }) => {
    if ($status === 'issued') return 'background-color: #d6efd5; color: #1b5e20;'
    if ($status === 'void') return 'background-color: #f9d6d6; color: #b71c1c;'
    return 'background-color: #fff3cd; color: #856404;'
  }}
`

const RectBanner = styled.div`
  padding: 12px 16px;
  background-color: #fff3cd;
  border-left: 4px solid #b71c1c;
  border-radius: ${theme.radius.sm};
  font-size: 13px;
  a { color: ${theme.colors.brandMain || '#ff5722'}; }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
  padding: 16px;
  background-color: ${theme.fill.defaultMain};
`

const CardTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
`

const KV = styled.dl`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px 12px;
  margin: 0 0 6px;
  font-size: 13px;
  dt {
    color: ${theme.colors.defaultWeak};
    font-weight: 500;
  }
  dd {
    margin: 0;
    color: ${theme.colors.defaultStrong};
    word-break: break-word;
  }
`

const Frozen = styled.p`
  margin: 8px 0 0;
  font-size: 11px;
  font-style: italic;
  color: ${theme.colors.defaultWeak};
`

const Section = styled.section`
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
  padding: 16px;
  background-color: ${theme.fill.defaultMain};
`

const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
`

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid ${theme.border.defaultWeak};
  }
  th {
    font-weight: 600;
    color: ${theme.colors.defaultWeak};
    font-size: 12px;
    text-transform: uppercase;
  }
  tbody tr:last-child td { border-bottom: none; }
`

const TotalsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`
